import React, { useEffect, useRef, useState } from "react"
import { useToaster } from "@hellocontento/maillard"
import { Text } from "rebass/styled-components"
import { Plus, X } from "styled-icons/feather"
import { callApi } from "utils/ContentoApi"

import * as styles from "../styles"
import GradientPicker from "./GradientPicker"
import ColorPickerPopover, { AnchorPoint } from "./ColorPickerPopover"
import QuoteSlide, { ClickTarget } from "./QuoteSlide"
import { ColorTheme, Fonts, VisualProfileSpec, measureLogo, uploadFileToSignedUrl } from "../visualProfile"

// Perceived luminance from a hex string (#RRGGBB or #RRGGBBAA). Anything
// below the midpoint reads as "dark" — the delete X then renders white so it
// stays visible.
const isDarkBackground = (hex: string): boolean => {
  const match = hex.match(/^#([0-9a-fA-F]{6})/)
  if (!match) return false
  const red = parseInt(match[1].slice(0, 2), 16)
  const green = parseInt(match[1].slice(2, 4), 16)
  const blue = parseInt(match[1].slice(4, 6), 16)
  return (0.299 * red + 0.587 * green + 0.114 * blue) < 140
}


export type ThemeKey = "light_theme" | "dark_theme"

const THEME_DISPLAY_NAMES: Record<ThemeKey, string> = {
  light_theme: "Light theme",
  dark_theme: "Dark theme",
}

type ThemeRowProps = {
  spec: VisualProfileSpec
  themeKey: ThemeKey
  // accountId + profileId are needed to construct the logo-upload-url
  // endpoint. The row issues the upload itself (signed-URL request + PUT
  // to GCS) and then emits the resulting URL through onThemeChange.
  accountId: string
  profileId: string
  // Emit a replacement theme (or `null` to delete the theme entirely). The
  // parent owns the visual-profile spec + the PUT call + conflict handling;
  // this row only knows how to edit one theme.
  onThemeChange: (themeKey: ThemeKey, nextTheme: ColorTheme | null) => void
  // Company name, shown as a text wordmark in the preview when the theme has no logo.
  logoFallbackBrandName: string
  // Whether this row is the one allowed to show its picker popovers. The parent
  // tracks a single active theme so opening one row's picker closes the other's.
  isActive: boolean
  onRequestActive: () => void
}

// Targets the colour picker can edit — the gradient case is handled by a
// different picker entirely so we exclude it here.
type ColorTarget = Exclude<ClickTarget, { kind: "accent_gradients" } | { kind: "logo" }>

const colorForTarget = (theme: ColorTheme, target: ColorTarget): string => {
  if (target.kind === "accent_colors") {
    return theme.accent_colors[target.index] ?? "#000000"
  }
  return theme[target.kind]
}

const applyColor = (theme: ColorTheme, target: ColorTarget, color: string): ColorTheme => {
  if (target.kind === "accent_colors") {
    const nextAccents = [...theme.accent_colors]
    nextAccents[target.index] = color
    return { ...theme, accent_colors: nextAccents }
  }
  return { ...theme, [target.kind]: color }
}

const ThemeRow: React.FC<ThemeRowProps> = ({
  spec,
  themeKey,
  accountId,
  profileId,
  onThemeChange,
  logoFallbackBrandName,
  isActive,
  onRequestActive,
}) => {
  const addToast = useToaster()
  const themeName = THEME_DISPLAY_NAMES[themeKey]
  const logoInputRef = useRef<HTMLInputElement>(null)
  const [editedTheme, setEditedTheme] = useState<ColorTheme>(spec[themeKey] as ColorTheme)
  // Each picker uses its anchor-point state as the "is open" signal — when
  // non-null the picker is rendered, when null it isn't. No separate boolean.
  const [colorPickerTarget, setColorPickerTarget] = useState<ColorTarget | null>(null)
  const [colorPickerAnchor, setColorPickerAnchor] = useState<AnchorPoint | null>(null)
  const [gradientPickerAnchor, setGradientPickerAnchor] = useState<AnchorPoint | null>(null)

  // Sync local edited state when the spec prop changes (e.g., after the
  // parent reconciled a save or accepted server state from a conflict).
  useEffect(() => {
    setEditedTheme(spec[themeKey] as ColorTheme)
  }, [spec, themeKey])

  const fonts: Fonts = { headline_font: spec.headline_font, body_font: spec.body_font }

  const accentColors = editedTheme.accent_colors.slice(0, 3) //in line with production prompt
  const gradient = editedTheme.accent_gradients?.[0] //in line with production prompt
  // One quote slide per accent (plain) + one per gradient. Same copy across
  // all of them — they're alternative renderings of the SAME post, not
  // sequential slides of one carousel.
  const variants: { value: string; accentIndex: number | null }[] = [
    ...(gradient ? [{ value: gradient, accentIndex: null }] : []),
    ...accentColors.map((value, i) => ({ value, accentIndex: i })),
  ]

  // QuoteSlide emits every click through here. We route to the appropriate
  // picker based on the click target — color clicks open the colour picker;
  // the gradient click opens the gradient picker; the logo click triggers
  // the hidden file input.
  const handleClick = (target: ClickTarget, point: AnchorPoint) => {
    if (target.kind === "accent_gradients") {
      onRequestActive()
      setGradientPickerAnchor(point)
      return
    }
    if (target.kind === "logo") {
      logoInputRef.current?.click()
      return
    }
    onRequestActive()
    setColorPickerTarget(target)
    setColorPickerAnchor(point)
  }

  const handleLogoFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    // Reset the input value immediately so re-selecting the same file
    // still triggers an onChange.
    event.target.value = ""
    if (!file) return
    try {
      const { width, height } = await measureLogo(file)
      const { signedUrl, publicUrl } = await callApi({
        method: "post",
        url: `content-factory/accounts/${accountId}/profiles/${profileId}/logo/upload-url`,
        data: { contentType: file.type },
      }, true, true)
      await uploadFileToSignedUrl(signedUrl, file)
      const nextTheme: ColorTheme = {
        ...editedTheme,
        logo: publicUrl,
        logo_natural_width: width,
        logo_natural_height: height,
      }
      setEditedTheme(nextTheme)
      onThemeChange(themeKey, nextTheme)
    } catch (err) {
      console.error("Failed to upload logo", { accountId, profileId, themeKey, err })
      addToast("Failed to upload logo", "error")
    }
  }

  // Live preview only — no persistence while the user is dragging the picker.
  const handleColorPickerChange = (color: string) => {
    if (!colorPickerTarget) return
    setEditedTheme(applyColor(editedTheme, colorPickerTarget, color))
  }

  const revertLivePreviewToLastSaved = () => setEditedTheme(spec[themeKey] as ColorTheme)

  const handleColorPickerSave = () => {
    setColorPickerTarget(null)
    setColorPickerAnchor(null)
    onThemeChange(themeKey, editedTheme)
  }

  const handleColorPickerCancel = () => {
    setColorPickerTarget(null)
    setColorPickerAnchor(null)
    revertLivePreviewToLastSaved()
  }

  // Live preview as the user edits stops/angle inside the GradientPicker.
  const handleGradientPickerChange = (nextGradient: string) => {
    const nextGradients = [...(editedTheme.accent_gradients ?? [])]
    nextGradients[0] = nextGradient
    setEditedTheme({ ...editedTheme, accent_gradients: nextGradients })
  }

  const handleGradientPickerClose = () => {
    setGradientPickerAnchor(null)
  }

  const handleGradientSave = () => {
    onThemeChange(themeKey, editedTheme)
  }

  const addAccentColor = (event: React.MouseEvent) => {
    event.stopPropagation()
    const defaultColor = editedTheme.accent_colors[0] ?? editedTheme.headline_color ?? "#6E6E6E"
    const nextAccents = [...editedTheme.accent_colors, defaultColor]
    setEditedTheme({ ...editedTheme, accent_colors: nextAccents })
    onRequestActive()
    setColorPickerTarget({ kind: "accent_colors", index: nextAccents.length - 1 })
    setColorPickerAnchor({ x: event.clientX, y: event.clientY })
  }

  // Delete a preview card. Plain accent → drop from accent_colors[i].
  // Gradient → drop accent_gradients[0].
  const handleDeleteVariant = (variant: { accentIndex: number | null }, event: React.MouseEvent) => {
    event.stopPropagation()
    let nextTheme: ColorTheme
    if (variant.accentIndex === null) {
      nextTheme = { ...editedTheme, accent_gradients: (editedTheme.accent_gradients ?? []).slice(1) }
    } else {
      const nextAccents = [...editedTheme.accent_colors]
      nextAccents.splice(variant.accentIndex, 1)
      nextTheme = { ...editedTheme, accent_colors: nextAccents }
    }
    setEditedTheme(nextTheme)
    onThemeChange(themeKey, nextTheme)
  }

  // Delete this entire theme from the spec.
  const handleDeleteTheme = (event: React.MouseEvent) => {
    event.stopPropagation()
    onThemeChange(themeKey, null)
  }

  return (
    <>
      <styles.ProfileSection>
        <styles.ThemeSectionHeader>
          <Text fontSize={14} fontWeight={600} color="darkBlack">{themeName}</Text>
          <styles.HeaderIconButton
            type="button"
            $danger
            onClick={handleDeleteTheme}
            aria-label={`Delete ${themeName.toLowerCase()}`}
          >
            <i className="icon-delete" style={{ fontSize: 18 }} />
          </styles.HeaderIconButton>
        </styles.ThemeSectionHeader>
        <styles.BrandCarousel>
          {variants.map((variant, i) => (
            <styles.SlideWithDelete key={`${variant.value}-${i}`}>
              <QuoteSlide
                theme={editedTheme}
                fonts={fonts}
                accent={variant.value}
                accentIndex={variant.accentIndex}
                logoFallbackBrandName={logoFallbackBrandName}
                onClick={handleClick}
              />
              <styles.PreviewDeleteButton
                type="button"
                $dark={isDarkBackground(editedTheme.background_color)}
                onClick={(event) => handleDeleteVariant(variant, event)}
                title="Delete this preview"
              >
                <X size={12} strokeWidth={2.5} />
              </styles.PreviewDeleteButton>
            </styles.SlideWithDelete>
          ))}
          {/* Cap accent_colors at 3 (matches the writer/prompt budget) —
              if the user already has 3, hide the "+" tile. */}
          {editedTheme.accent_colors.length < 3 && (
            <styles.AddSlide type="button" onClick={addAccentColor} title="Add accent colour">
              <Plus size={24} />
            </styles.AddSlide>
          )}
        </styles.BrandCarousel>
      </styles.ProfileSection>

      <input
        ref={logoInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,image/avif"
        style={{ display: "none" }}
        onChange={handleLogoFileChange}
        aria-hidden
      />

      {isActive && colorPickerTarget && colorPickerAnchor && (
        <ColorPickerPopover
          colorValue={colorForTarget(editedTheme, colorPickerTarget)}
          onChange={handleColorPickerChange}
          onCancel={handleColorPickerCancel}
          onSave={handleColorPickerSave}
          anchorPoint={colorPickerAnchor}
        />
      )}

      {isActive && gradientPickerAnchor && gradient && (
        <GradientPicker
          gradientValue={editedTheme.accent_gradients?.[0] ?? gradient}
          onChange={handleGradientPickerChange}
          onClose={handleGradientPickerClose}
          onStopSave={handleGradientSave}
          onStopCancel={revertLivePreviewToLastSaved}
          anchorPoint={gradientPickerAnchor}
        />
      )}
    </>
  )
}

export default ThemeRow
